import { useEffect, useState } from 'react';
import { invoiceAPI, repairAPI, garageAPI, partAPI, referenceAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, Modal, Input, Select, Loading, Alert } from '../components/ui';
import { formatRs } from '../utils/currency';

const jobNo = (id) => `JOB-${String(id).padStart(4, '0')}`;
const fmtDT = (v) => v ? new Date(v).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : null;

const STATUS_META = {
  pending_approval: { label: 'Pending Foreman Approval', cls: 'bg-yellow-100 text-yellow-800' },
  pending_forward:  { label: 'Send to Finance Division', cls: 'bg-orange-100 text-orange-700' },
  pending_payment:  { label: 'Pending Payment (Accounts)', cls: 'bg-blue-100 text-blue-700' },
  completed:        { label: 'Completed', cls: 'bg-green-100 text-green-700' },
  rejected:         { label: 'Rejected', cls: 'bg-red-100 text-red-700' },
};

// ── Printable invoice / payment voucher ──────────────────────────────────────
const printVoucher = (inv) => {
  const existing = document.getElementById('inv-print-frame');
  if (existing) existing.remove();
  const iframe = document.createElement('iframe');
  iframe.id = 'inv-print-frame';
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(iframe);
  const rs = (v) => v == null || v === '' ? '—' : 'Rs. ' + Number(v).toLocaleString('en-LK', { minimumFractionDigits: 2 });
  const r = (l, v) => `<tr><th>${l}</th><td>${v ?? '—'}</td></tr>`;
  const stage = (label, done, who, when) =>
    `<tr><td style="width:60%">${label}</td>
       <td style="text-align:center;width:10%">${done ? '✔' : '☐'}</td>
       <td>${who || ''}${when ? '<br/><span style="color:#666;font-size:11px">' + new Date(when).toLocaleString('en-GB') + '</span>' : ''}</td></tr>`;
  const veh = inv.repair?.vehicle;
  const html = `
    <h2>Invoice / Payment Voucher</h2>
    <table>
      ${r('Job Order', 'JOB-' + String(inv.repair_id).padStart(4,'0'))}
      ${r('Vehicle No.', veh?.reg_number)}
      ${r('Vehicle Make &amp; Model', veh ? `${veh.brand || ''} ${veh.model || ''}`.trim() : '—')}
      ${r('Supplier Name', inv.supplier?.name)}
      ${r('Supplier Address', inv.supplier?.location)}
      ${r('Invoice No.', inv.invoice_no)}
      ${r('Invoice Date', inv.invoice_date)}
      ${r('Mileage', inv.mileage_km ? inv.mileage_km + ' km' : '—')}
      ${r('Amount', rs(inv.amount))}
      ${r('VAT / Tax Amount', rs(inv.vat_amount))}
      ${r('Total', '<strong>' + rs(inv.total_amount || inv.amount) + '</strong>')}
    </table>

    <div class="sec">Verification &amp; Approval</div>
    <table class="stages">
      ${stage('Invoice verified by Mechanical Foreman', ['pending_payment','completed'].includes(inv.status), inv.reviewer?.name, inv.reviewed_at)}
      ${stage('Invoice forwarded for payment approval', ['pending_payment','completed'].includes(inv.status), inv.reviewer?.name, inv.reviewed_at)}
      ${stage('Invoice forwarded to Finance Division', ['pending_payment','completed'].includes(inv.status), '', inv.reviewed_at)}
      ${stage('Payment done', inv.status === 'completed', inv.payer?.name, inv.paid_at)}
    </table>

    <div class="sec">Payment Details</div>
    <table>
      ${r('Cheque / Cash', inv.payment_method ? (inv.payment_method === 'cash' ? 'Cash' : 'Cheque') : '—')}
      ${r('Amount', rs(inv.paid_amount))}
      ${r('Cheque No', inv.cheque_no)}
      ${r('Cheque Date', inv.cheque_date)}
      ${r("Cheque's Bank", inv.cheque_bank)}
      ${r('Receipt No.', inv.receipt_no)}
    </table>

    <table class="sign">
      <tr>
        <td><div class="line">Transport Officer</div></td>
        <td><div class="line">Mechanical Foreman</div></td>
        <td><div class="line">Finance Division</div></td>
      </tr>
    </table>`;
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`<html><head><title>Invoice Voucher</title><style>
    body{font-family:Arial,sans-serif;font-size:13px;padding:45px;max-width:800px;margin:0 auto;}
    h2{text-align:center;text-transform:uppercase;letter-spacing:1px;margin:0 0 20px;}
    table{width:100%;border-collapse:collapse;margin-bottom:16px;}
    th{background:#f2f2f2;text-align:left;padding:6px 10px;border:1px solid #ccc;width:35%;}
    td{padding:6px 10px;border:1px solid #ccc;}
    .sec{font-weight:bold;background:#e8e4f3;padding:6px 10px;border:1px solid #ccc;}
    .stages td{font-size:12px;}
    .sign td{border:0;padding-top:52px;}
    .line{border-top:1px solid #333;width:210px;padding-top:4px;font-size:12px;}
    @media print { @page { margin: 1.8cm; } }
  </style></head><body>${html}</body></html>`);
  doc.close();
  setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 400);
};

export default function Invoices() {
  const { can } = useAuth();
  const [items,   setItems]   = useState([]);
  const [jobs,    setJobs]    = useState([]);
  const [garages, setGarages] = useState([]);
  const [tyreSizes, setTyreSizes] = useState([]);
  const [batSizes,  setBatSizes]  = useState([]);
  const [partMeta, setPartMeta] = useState(null);  // { part_type, vehicle_id, repair_id }
  const [units, setUnits] = useState([]);          // one row per battery / tyre
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [success, setSuccess] = useState('');
  const [filter,  setFilter]  = useState('');
  const [saving,  setSaving]  = useState(false);

  const [modal,   setModal]   = useState(false);   // create / edit
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ repair_id:'', garage_id:'', invoice_no:'', invoice_date:'', mileage_km:'', amount:'', vat_amount:'', description:'' });

  const [paying, setPaying] = useState(null);       // accounts
  const [payForm, setPayForm] = useState({ paid_amount:'', cheque_no:'', payment_method:'cheque', cheque_date:'', cheque_bank:'', receipt_no:'' });

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3500); };

  const load = () => {
    setLoading(true);
    Promise.all([
      invoiceAPI.getAll(),
      repairAPI.getAll({ limit: 200 }).catch(() => ({ data: { data: [] } })),
      garageAPI.getAll().catch(() => ({ data: { data: [] } })),
      referenceAPI.getTyreSizes().catch(() => ({ data: { data: [] } })),
      referenceAPI.getBatterySizes().catch(() => ({ data: { data: [] } })),
    ])
      .then(([i, r, g, ts, bs]) => {
        setTyreSizes(ts.data.data || []);
        setBatSizes(bs.data.data || []);
        setGarages(g.data.data || []);
        const invs = i.data.data || [];
        setItems(invs);
        // Jobs: foreman-approved, and not already carrying an active invoice
        const invoicedJobIds = new Set(invs.filter(x => x.status !== 'rejected').map(x => x.repair_id));
        // Only jobs the manager has signed off (award letter issued) can be invoiced,
        // and only while they don't already carry an invoice.
        setJobs((r.data.data || []).filter(x =>
          ['approved','in_progress','completed'].includes(x.status) &&
          x.manager_status === 'approved' &&
          !invoicedJobIds.has(x.id)
        ));
      })
      .catch(() => setError('Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditing(null); setForm({ repair_id:'', garage_id:'', invoice_no:'', invoice_date: new Date().toISOString().split('T')[0], mileage_km:'', amount:'', vat_amount:'', description:'' }); setModal(true); };
  const openEdit   = (inv) => {
    setEditing(inv);
    setForm({ repair_id: inv.repair_id, garage_id: inv.garage_id || '', invoice_no: inv.invoice_no,
      invoice_date: inv.invoice_date || '', mileage_km: inv.mileage_km || '',
      amount: inv.amount, vat_amount: inv.vat_amount || '', description: inv.description || '' });
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await invoiceAPI.update(editing.id, form); flash('Invoice re-submitted for foreman approval'); }
      else {
        const res = await invoiceAPI.create(form);
        if (partMeta && units.length) {
          const each = units.filter(u => u.brand || u.serial_no || u.size);
          const per = each.length ? (Number(form.amount) || 0) / each.length : 0;
          for (const u of each) {
            await partAPI.create({
              ...partMeta, ...u, quantity: 1,
              invoice_id: res.data.data?.id,
              amount: per ? per.toFixed(2) : null,
            }).catch(() => {});
          }
        }
        flash('Invoice submitted — forwarded to foreman');
      }
      setModal(false); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save invoice'); }
    finally { setSaving(false); }
  };

  const submitPay = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await invoiceAPI.pay(paying.id, payForm);
      flash('Payment recorded — invoice completed');
      setPaying(null); load();
    } catch (err) { setError(err.response?.data?.message || 'Failed to record payment'); }
    finally { setSaving(false); }
  };

  const del = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_no}?`)) return;
    try { await invoiceAPI.remove(inv.id); flash('Invoice deleted'); load(); }
    catch (err) { setError(err.response?.data?.message || 'Delete failed'); }
  };

  const visible = items.filter(i => !filter || i.status === filter);

  return (
    <div className="space-y-4">
      <PageHeader title="Invoices" subtitle={`${items.length} invoice${items.length === 1 ? '' : 's'}`}
        action={can('admin','transport_officer') && <Button onClick={openCreate}>+ New Invoice</Button>} />
      {error   && <Alert type="error"   onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess('')}>{success}</Alert>}

      <div className="flex gap-2 flex-wrap">
        <button onClick={() => setFilter('')}
          className={`px-3 py-1.5 rounded-full text-sm border ${!filter ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
          All ({items.length})
        </button>
        {Object.entries(STATUS_META).map(([k, m]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full text-sm border ${filter === k ? 'bg-primary-600 text-white border-primary-600' : 'bg-white'}`}>
            {m.label} ({items.filter(i => i.status === k).length})
          </button>
        ))}
      </div>

      {loading ? <Loading /> : (
        <Table headers={['Job / Vehicle','Invoice No','Amount','Status','Payment','Actions']}
          empty="No invoices yet">
          {visible.map(inv => (
            <tr key={inv.id} className="border-b last:border-0 hover:bg-gray-50">
              <td className="px-4 py-3">
                <p className="font-mono text-sm font-semibold text-primary-700">{jobNo(inv.repair_id)} · {inv.repair?.vehicle?.reg_number || '—'}</p>
                <p className="text-xs text-gray-400">{inv.repair?.title}</p>
                <p className="text-xs text-gray-400">by {inv.submitter?.name}</p>
              </td>
              <td className="px-4 py-3 text-sm">{inv.invoice_no}
                {inv.description && <p className="text-xs text-gray-400 max-w-[180px] truncate" title={inv.description}>{inv.description}</p>}
              </td>
              <td className="px-4 py-3 text-sm font-medium">
                {formatRs(inv.total_amount || inv.amount)}
                {inv.vat_amount ? <p className="text-xs text-gray-400">incl. VAT {formatRs(inv.vat_amount)}</p> : null}
                {inv.supplier?.name && <p className="text-xs text-gray-400">{inv.supplier.name}</p>}
              </td>
              <td className="px-4 py-3">
                <span className={`px-2 py-1 rounded-full text-xs ${STATUS_META[inv.status]?.cls || ''}`}>
                  {STATUS_META[inv.status]?.label || inv.status}
                </span>
                {inv.status === 'rejected' && inv.review_remark && (
                  <p className="text-xs text-red-500 mt-1 max-w-[200px]">✖ {inv.review_remark}</p>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Submitted {fmtDT(inv.created_at)}
                  {inv.reviewed_at && <> · Reviewed {fmtDT(inv.reviewed_at)}</>}
                  {inv.forwarded_at && <> · Sent to Finance {fmtDT(inv.forwarded_at)}</>}
                </p>
              </td>
              <td className="px-4 py-3 text-sm">
                {inv.status === 'completed' ? (
                  <>
                    <p>{formatRs(inv.paid_amount)}</p>
                    <p className="text-xs text-gray-400">
                      {inv.payment_method === 'cash' ? 'Cash' : `Cheque ${inv.cheque_no || ''}`}
                      {inv.cheque_bank ? ` · ${inv.cheque_bank}` : ''}
                    </p>
                    {inv.receipt_no && <p className="text-xs text-gray-400">Receipt {inv.receipt_no}</p>}
                    <p className="text-xs text-gray-400">{fmtDT(inv.paid_at)}</p>
                  </>
                ) : <span className="text-gray-300">—</span>}
              </td>
              <td className="px-4 py-3">
                <div className="flex gap-1 flex-wrap">
                  <Button variant="ghost" size="sm" title="Print invoice / payment voucher"
                    onClick={() => printVoucher(inv)}>📄</Button>
                  {can('admin','foreman') && inv.status === 'pending_approval' && (
                    <span className="text-xs text-primary-600 self-center" title="Review this in the Approvals menu">→ Approvals</span>
                  )}
                  {can('transport_officer') && inv.status === 'pending_approval' && (
                    <span className="text-xs text-gray-400 self-center">🔒 Awaiting foreman</span>
                  )}
                  {can('admin','transport_officer','manager') && inv.status === 'pending_forward' && (
                    <Button size="sm"
                      onClick={async () => {
                        if (!window.confirm(`Forward invoice ${inv.invoice_no} to the Finance Division for payment?`)) return;
                        try { await invoiceAPI.forward(inv.id); flash('Forwarded to Finance Division'); load(); }
                        catch (err) { setError(err.response?.data?.message || 'Failed to forward'); }
                      }}>➡️ Send to Finance</Button>
                  )}
                  {can('accountant') && inv.status === 'pending_forward' && (
                    <span className="text-xs text-gray-400 self-center">⏳ Awaiting officer forward</span>
                  )}
                  {can('admin','accountant') && inv.status === 'pending_payment' && (
                    <Button size="sm"
                      onClick={() => { setPaying(inv); setPayForm({ paid_amount: inv.total_amount || inv.amount, cheque_no:'', payment_method:'cheque', cheque_date: new Date().toISOString().split('T')[0], cheque_bank:'', receipt_no:'' }); }}>💳 Record Payment</Button>
                  )}
                  {can('admin','transport_officer') && inv.status === 'rejected' && (
                    <>
                      <Button variant="ghost" size="sm" title="Edit & re-submit" onClick={() => openEdit(inv)}>✏️</Button>
                      <Button variant="ghost" size="sm" title="Delete" onClick={() => del(inv)}>🗑️</Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      {/* ══ CREATE / EDIT (officer) ═══════════════════════════════════════════ */}
      <Modal open={modal} onClose={() => setModal(false)}
        title={editing ? `Edit Invoice ${editing.invoice_no} (re-submit)` : 'New Invoice'}>
        <form onSubmit={save} className="space-y-4">
          <Select label="Job No *" value={form.repair_id} disabled={!!editing}
            onChange={e => {
              const j = jobs.find(x => String(x.id) === e.target.value);
              setForm({ ...form, repair_id: e.target.value,
                garage_id: j?.garage_id || form.garage_id,
                mileage_km: j?.mileage_km || form.mileage_km });
              // Battery / tyre jobs capture warranty details with the invoice
              if (j && ['battery','tyre'].includes(j.repair_type)) {
                const isTyre = j.repair_type === 'tyre';
                const qty = Number(isTyre ? (j.tyre_qty || 4) : (j.bat_qty || 1)) || 1;
                const size = isTyre ? (j.tyre_size || '') : (j.bat_size || '');
                setPartMeta({ part_type: j.repair_type, vehicle_id: j.vehicle_id, repair_id: j.id });
                setUnits(Array.from({ length: Math.min(qty, 12) }, () => ({
                  brand:'', size, capacity_ah:'', serial_no:'',
                  install_date: new Date().toISOString().split('T')[0],
                  warranty_months: 12, prorata_months: 0,
                })));
              } else { setPartMeta(null); setUnits([]); }
            }} required>
            <option value="">Select job…</option>
            {jobs.map(j => (
              <option key={j.id} value={j.id}>
                {jobNo(j.id)} — {j.vehicle?.reg_number} · {j.title} ({j.repair_type})
              </option>
            ))}
          </Select>
          <Select label="Supplier / Garage" value={form.garage_id}
            onChange={e => setForm({ ...form, garage_id: e.target.value })}>
            <option value="">Select supplier…</option>
            {garages.map(g => <option key={g.id} value={g.id}>{g.name}{g.location ? ` — ${g.location}` : ''}</option>)}
          </Select>
          <div className="grid grid-cols-2 gap-4">
            <Input label="Invoice No *" required value={form.invoice_no}
              onChange={e => setForm({ ...form, invoice_no: e.target.value })} placeholder="INV-2026-001" />
            <Input label="Invoice Date" type="date" value={form.invoice_date}
              onChange={e => setForm({ ...form, invoice_date: e.target.value })} />
            <Input label="Mileage (km)" type="number" value={form.mileage_km}
              onChange={e => setForm({ ...form, mileage_km: e.target.value })} />
            <Input label="Amount (Rs.) *" type="number" step="0.01" required value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })} />
            <Input label="VAT / Tax Amount (Rs.)" type="number" step="0.01" value={form.vat_amount}
              onChange={e => setForm({ ...form, vat_amount: e.target.value })} />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total (Rs.)</label>
              <div className="px-3 py-2 rounded-lg border bg-gray-50 text-sm font-semibold">
                {formatRs((Number(form.amount) || 0) + (Number(form.vat_amount) || 0))}
              </div>
            </div>
          </div>
          {partMeta && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                  {partMeta.part_type === 'battery' ? '🔋 Battery Details' : '⭕ Tyre Details'} — one row per unit, for warranty tracking
                </p>
                <button type="button" className="text-xs text-primary-600 hover:underline"
                  onClick={() => setUnits(u => [...u, {
                    brand: u[0]?.brand || '', size: u[0]?.size || '', capacity_ah: u[0]?.capacity_ah || '', serial_no:'',
                    install_date: u[0]?.install_date || new Date().toISOString().split('T')[0],
                    warranty_months: u[0]?.warranty_months || 12, prorata_months: u[0]?.prorata_months || 0,
                  }])}>+ Add another</button>
              </div>

              <div className="space-y-3">
                {units.map((u, i) => (
                  <div key={i} className="bg-white rounded-lg border border-amber-200 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-500">
                        {partMeta.part_type === 'battery' ? 'Battery' : 'Tyre'} #{i + 1}
                      </span>
                      {units.length > 1 && (
                        <button type="button" className="text-xs text-red-500 hover:underline"
                          onClick={() => setUnits(us => us.filter((_, x) => x !== i))}>remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      <Input label="Brand" value={u.brand}
                        onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, brand: e.target.value } : x))}
                        placeholder="e.g. LUCAS, EXIDE" />
                      <Select label="Size" value={u.size}
                        onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, size: e.target.value } : x))}>
                        <option value="">Select…</option>
                        {(partMeta.part_type === 'tyre' ? tyreSizes : batSizes).map(x => {
                          const val = partMeta.part_type === 'tyre' ? x.tyre_size : x.bat_size;
                          return <option key={x.id} value={val}>{val}</option>;
                        })}
                        {u.size && !(partMeta.part_type === 'tyre' ? tyreSizes : batSizes)
                          .some(x => (partMeta.part_type === 'tyre' ? x.tyre_size : x.bat_size) === u.size) && (
                          <option value={u.size}>{u.size}</option>
                        )}
                      </Select>
                      {partMeta.part_type === 'battery' ? (
                        <Input label="Ah" value={u.capacity_ah}
                          onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, capacity_ah: e.target.value } : x))}
                          placeholder="e.g. 45" />
                      ) : <div />}
                      <Input label="Serial No." value={u.serial_no}
                        onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, serial_no: e.target.value } : x))}
                        placeholder="unique per unit" />
                      <Input label={partMeta.part_type === 'battery' ? 'Battery-in Date' : 'Fitted Date'} type="date"
                        value={u.install_date}
                        onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, install_date: e.target.value } : x))} />
                      <Input label="Warranty (months)" type="number" min="0" value={u.warranty_months}
                        onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, warranty_months: e.target.value } : x))} />
                      <Input label="Pro-rata (months)" type="number" min="0" value={u.prorata_months}
                        onChange={e => setUnits(us => us.map((x, k) => k === i ? { ...x, prorata_months: e.target.value } : x))} />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Warranty end dates are calculated from each install date and appear in Renewals → Battery / Tyre Warranty.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea className="w-full border rounded-lg px-3 py-2 text-sm" rows="3"
              placeholder="Work carried out / items covered by this invoice"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <p className="text-xs text-gray-500">Submitting forwards this invoice to the foreman for approval.</p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editing ? 'Re-submit' : 'Submit to Foreman'}</Button>
          </div>
        </form>
      </Modal>

      {/* ══ ACCOUNTS PAYMENT ══════════════════════════════════════════════════ */}
      <Modal open={!!paying} onClose={() => setPaying(null)}
        title={`Record Payment: ${paying?.invoice_no || ''}`}>
        {paying && (
          <form onSubmit={submitPay} className="space-y-4">
            <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1">
              <p><strong>Job:</strong> {jobNo(paying.repair_id)} — {paying.repair?.vehicle?.reg_number}</p>
              <p><strong>Invoice amount:</strong> {formatRs(paying.amount)}
                {paying.vat_amount ? <> + VAT {formatRs(paying.vat_amount)} = <strong>{formatRs(paying.total_amount || paying.amount)}</strong></> : null}</p>
              {paying.supplier?.name && <p><strong>Supplier:</strong> {paying.supplier.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Select label="Payment Method *" value={payForm.payment_method}
                onChange={e => setPayForm({ ...payForm, payment_method: e.target.value })}>
                <option value="cheque">Cheque</option>
                <option value="cash">Cash</option>
              </Select>
              <Input label="Paid Amount (Rs.) *" type="number" step="0.01" required
                value={payForm.paid_amount}
                onChange={e => setPayForm({ ...payForm, paid_amount: e.target.value })} />
              {payForm.payment_method === 'cheque' && (
                <>
                  <Input label="Cheque No *" required
                    value={payForm.cheque_no}
                    onChange={e => setPayForm({ ...payForm, cheque_no: e.target.value })} />
                  <Input label="Cheque Date" type="date"
                    value={payForm.cheque_date}
                    onChange={e => setPayForm({ ...payForm, cheque_date: e.target.value })} />
                  <Input label="Cheque's Bank"
                    value={payForm.cheque_bank}
                    onChange={e => setPayForm({ ...payForm, cheque_bank: e.target.value })}
                    placeholder="e.g. BOC — Colombo Main" />
                </>
              )}
              <Input label="Receipt No."
                value={payForm.receipt_no}
                onChange={e => setPayForm({ ...payForm, receipt_no: e.target.value })} />
            </div>
            <p className="text-xs text-gray-500">
              The same cheque no can be used on several invoices when one cheque covers a combined payment.
              Recording the cheque completes this invoice's flow.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={() => setPaying(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Complete Invoice'}</Button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
